/**
 * Comprados Juntos Component
 * Gerencia seleção de produtos, variantes e personalização.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Modal de Personalização
// ─────────────────────────────────────────────────────────────────────────────

class CompradosJuntosPersonalizacaoModal {
    constructor(component) {
        this.component = component;
        this.modal = null;
        this.currentQueue = [];
        this.currentIndex = 0;
        this.collectedProperties = new Map();
        this._resolveModal = null;
        this._rejectModal = null;
    }

    _ensureModal() {
        if (document.getElementById('comprados-juntos-personalizacao-modal')) {
            this.modal = document.getElementById('comprados-juntos-personalizacao-modal');
            return;
        }

        const overlay = document.createElement('div');
        overlay.id = 'comprados-juntos-personalizacao-modal';
        overlay.className = 'cj-pers-modal';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'cj-pers-modal-title');
        overlay.setAttribute('hidden', '');
        overlay.innerHTML = `
            <div class="cj-pers-modal__backdrop"></div>
            <div class="cj-pers-modal__dialog">
                <button class="cj-pers-modal__close" type="button" aria-label="Fechar">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
                <div class="cj-pers-modal__header">
                    <p class="cj-pers-modal__step"></p>
                    <h2 id="cj-pers-modal-title" class="cj-pers-modal__title"></h2>
                </div>
                <div class="cj-pers-modal__body"></div>
                <div class="cj-pers-modal__footer">
                    <button class="cj-pers-modal__btn-confirm button button--primary" type="button"></button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);
        this.modal = overlay;

        overlay.querySelector('.cj-pers-modal__backdrop').addEventListener('click', () => this._cancel());
        overlay.querySelector('.cj-pers-modal__close').addEventListener('click', () => this._cancel());
    }

    open(queue) {
        return new Promise((resolve, reject) => {
            this._resolveModal = resolve;
            this._rejectModal = reject;
            this.currentQueue = queue;
            this.currentIndex = 0;
            this.collectedProperties.clear();

            this._ensureModal();
            this._showStep(0);
        });
    }

    async _showStep(index) {
        const item = this.currentQueue[index];
        if (!item) return;

        const total = this.currentQueue.length;
        const titleEl = this.modal.querySelector('.cj-pers-modal__title');
        const stepEl = this.modal.querySelector('.cj-pers-modal__step');
        const bodyEl = this.modal.querySelector('.cj-pers-modal__body');
        const confirmBtn = this.modal.querySelector('.cj-pers-modal__btn-confirm');

        stepEl.textContent = total > 1 ? `Personalização ${index + 1} de ${total}` : 'Personalização do produto';
        titleEl.textContent = item.productTitle;
        bodyEl.innerHTML = '<div class="cj-pers-modal__loading">Carregando...</div>';
        confirmBtn.textContent = index < total - 1 ? 'Próximo →' : 'Confirmar e Adicionar';

        this.modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';

        const newConfirm = confirmBtn.cloneNode(true);
        newConfirm.textContent = confirmBtn.textContent;
        confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);

        try {
            const html = await this._fetchProductPersonalizacao(item.productUrl);
            if (html) {
                bodyEl.innerHTML = '';
                bodyEl.appendChild(html);
                this._setupForm(bodyEl, item);
            } else {
                bodyEl.innerHTML = '<p class="cj-pers-modal__error">Não foi possível carregar a personalização.</p>';
            }
        } catch (e) {
            console.error('[CompradosJuntos] Erro ao carregar personalização:', e);
            bodyEl.innerHTML = '<p class="cj-pers-modal__error">Erro ao carregar personalização.</p>';
        }

        const finalBtn = this.modal.querySelector('.cj-pers-modal__btn-confirm');
        finalBtn.addEventListener('click', () => this._confirmStep(index));
    }

    async _fetchProductPersonalizacao(productUrl) {
        const response = await fetch(productUrl);
        if (!response.ok) return null;

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const persBlock = doc.querySelector('personalizacao-produto-block');
        if (!persBlock) return null;

        const container = document.createDocumentFragment();
        const wrapper = document.createElement('div');
        wrapper.className = 'cj-pers-modal__fields';

        const cloned = persBlock.cloneNode(true);
        cloned.removeAttribute('style');

        wrapper.appendChild(cloned);
        container.appendChild(wrapper);
        return container;
    }

    _setupForm(bodyEl, item) {
        bodyEl.querySelectorAll('[form]').forEach(el => el.removeAttribute('form'));

        bodyEl.querySelectorAll('input[maxlength], textarea[maxlength]').forEach(input => {
            const counter = bodyEl.querySelector(`[data-for="${input.id}"]`);
            if (!counter) return;
            input.addEventListener('input', () => {
                counter.textContent = `${input.value.length}/${input.maxLength}`;
            });
        });
    }

    _confirmStep(index) {
        const item = this.currentQueue[index];
        const bodyEl = this.modal.querySelector('.cj-pers-modal__body');

        const inputs = bodyEl.querySelectorAll('input[name], textarea[name]');
        const properties = {};

        let hasRequiredEmpty = false;
        inputs.forEach(input => {
            if (input.required && !input.value.trim()) {
                hasRequiredEmpty = true;
                input.classList.add('cj-pers-modal__input-error');
                input.addEventListener('input', () => input.classList.remove('cj-pers-modal__input-error'), { once: true });
            } else {
                const match = input.name.match(/^properties\[(.+)\]$/);
                const propName = match ? match[1] : input.name;
                if (input.value.trim()) {
                    properties[propName] = input.value.trim();
                }
            }
        });

        if (hasRequiredEmpty) {
            const existingMsg = bodyEl.querySelector('.cj-pers-modal__validation-msg');
            if (!existingMsg) {
                const msg = document.createElement('p');
                msg.className = 'cj-pers-modal__validation-msg';
                msg.textContent = 'Por favor, preencha os campos obrigatórios.';
                bodyEl.insertAdjacentElement('afterbegin', msg);
                setTimeout(() => msg.remove(), 3000);
            }
            return;
        }

        this.collectedProperties.set(item.variantId, {
            properties,
            productTitle: item.productTitle
        });

        const nextIndex = index + 1;
        if (nextIndex < this.currentQueue.length) {
            this._showStep(nextIndex);
        } else {
            this._finish();
        }
    }

    _cancel() {
        this._close();
        if (this._rejectModal) this._rejectModal(new Error('cancelled'));
    }

    _finish() {
        this._close();
        if (this._resolveModal) this._resolveModal(this.collectedProperties);
    }

    _close() {
        if (this.modal) this.modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

class CompradosJuntosComponent extends HTMLElement {
    constructor() {
        super();
        this.selectedProducts = new Map();
        this.personalizacaoModal = new CompradosJuntosPersonalizacaoModal(this);
    }

    connectedCallback() {
        this.currentVariantId = this.dataset.currentVariantId;
        this.currentPrice = parseInt(this.dataset.currentPrice, 10) || 0;

        this.selectedProducts.set(this.currentVariantId, {
            id: this.currentVariantId,
            quantity: 1,
            price: this.currentPrice
        });

        this.totalElement = this.querySelector('[ref="totalValue"]');
        this.addButton = this.querySelector('[ref="addButton"]');
        this.originalButtonText = this.addButton?.textContent || 'Adicionar ao Carrinho';

        this.setupCheckboxes();
        this.setupVariantSelectors();
        this.updateTotal();
        this.setupAddButton();
    }

    setupCheckboxes() {
        const checkboxes = this.querySelectorAll('.comprados-juntos-card__checkbox');

        checkboxes.forEach(checkbox => {
            const variantId = checkbox.dataset.variantId;
            const price = parseInt(checkbox.dataset.price, 10) || 0;
            const isCurrent = checkbox.dataset.isCurrent === 'true';

            if (checkbox.checked) {
                this.selectedProducts.set(variantId, {
                    id: variantId,
                    quantity: 1,
                    price: price
                });
            }

            if (isCurrent) return;

            checkbox.addEventListener('change', () => {
                const currentVariantId = checkbox.dataset.variantId;
                const currentPrice = parseInt(checkbox.dataset.price, 10) || 0;

                if (checkbox.checked) {
                    this.selectedProducts.set(currentVariantId, {
                        id: currentVariantId,
                        quantity: 1,
                        price: currentPrice
                    });
                } else {
                    this.selectedProducts.delete(currentVariantId);
                }

                this.updateTotal();
            });
        });

        this.updateTotal();
    }

    setupVariantSelectors() {
        const cards = this.querySelectorAll('.comprados-juntos-card[data-has-variants="true"]');

        cards.forEach(card => {
            const variantsScript = card.querySelector('.comprados-juntos-card__variants-data');
            if (!variantsScript) return;

            let variants;
            try {
                variants = JSON.parse(variantsScript.textContent);
            } catch (e) {
                console.error('[CompradosJuntos] Erro ao parsear variantes:', e);
                return;
            }

            card._variants = variants;

            const optionBtns = card.querySelectorAll('.comprados-juntos-card__option-btn');
            optionBtns.forEach(btn => {
                btn.addEventListener('click', () => {
                    const optionIndex = parseInt(btn.dataset.optionIndex, 10);
                    const value = btn.dataset.value;

                    const siblings = btn.closest('.comprados-juntos-card__option-values').querySelectorAll('.comprados-juntos-card__option-btn');
                    siblings.forEach(s => s.classList.remove('is-selected'));
                    btn.classList.add('is-selected');

                    this._onVariantChange(card, variants);
                });
            });
        });
    }

    _getSelectedOptions(card) {
        const options = [];
        const optionGroups = card.querySelectorAll('.comprados-juntos-card__option');
        optionGroups.forEach(group => {
            const selected = group.querySelector('.comprados-juntos-card__option-btn.is-selected');
            options.push(selected ? selected.dataset.value : null);
        });
        return options;
    }

    _findVariant(variants, selectedOptions) {
        return variants.find(v =>
            v.options.every((opt, i) => opt === selectedOptions[i])
        ) || null;
    }

    _onVariantChange(card, variants) {
        const selectedOptions = this._getSelectedOptions(card);
        const matchedVariant = this._findVariant(variants, selectedOptions);

        if (!matchedVariant) return;

        const oldVariantId = card.dataset.variantId;
        const newVariantId = String(matchedVariant.id);
        const newPrice = matchedVariant.price;
        const comparePrice = matchedVariant.compare_at_price;

        card.dataset.variantId = newVariantId;
        card.dataset.price = newPrice;

        // Atualizar URL do produto com variante
        const baseUrl = card.dataset.productUrl.split('?')[0];
        card.dataset.productUrl = `${baseUrl}?variant=${newVariantId}`;

        // Atualizar checkbox
        const checkbox = card.querySelector('.comprados-juntos-card__checkbox');
        if (checkbox) {
            checkbox.dataset.variantId = newVariantId;
            checkbox.dataset.price = newPrice;
        }

        // Atualizar preço exibido
        const priceContainer = card.querySelector('.comprados-juntos-card__price');
        if (priceContainer) {
            const hasDiscount = comparePrice && comparePrice > newPrice;
            let priceHtml = '';
            if (hasDiscount) {
                priceHtml += `<span class="comprados-juntos-card__compare-price">${this.formatMoney(comparePrice)}</span>`;
            }
            priceHtml += `<span class="comprados-juntos-card__current-price${hasDiscount ? ' comprados-juntos-card__current-price--sale' : ''}">${this.formatMoney(newPrice)}</span>`;
            priceContainer.innerHTML = priceHtml;
        }

        // Atualizar imagem se a variante tiver imagem própria
        if (matchedVariant.featured_image) {
            const img = card.querySelector('.comprados-juntos-card__image');
            if (img) {
                img.src = matchedVariant.featured_image.src;
                img.alt = matchedVariant.featured_image.alt || '';
            }
        }

        // Marcar opções indisponíveis
        this._updateOptionAvailability(card, variants);

        // Atualizar selectedProducts se estava selecionado
        if (this.selectedProducts.has(oldVariantId)) {
            this.selectedProducts.delete(oldVariantId);
            this.selectedProducts.set(newVariantId, {
                id: newVariantId,
                quantity: 1,
                price: newPrice
            });
        }

        this.updateTotal();
    }

    _updateOptionAvailability(card, variants) {
        const selectedOptions = this._getSelectedOptions(card);
        const optionGroups = card.querySelectorAll('.comprados-juntos-card__option');

        optionGroups.forEach((group, groupIndex) => {
            const btns = group.querySelectorAll('.comprados-juntos-card__option-btn');
            btns.forEach(btn => {
                const testOptions = [...selectedOptions];
                testOptions[groupIndex] = btn.dataset.value;

                const hasAvailable = variants.some(v =>
                    v.available && v.options.every((opt, i) => {
                        if (i === groupIndex) return opt === btn.dataset.value;
                        if (selectedOptions[i] === null) return true;
                        return opt === selectedOptions[i];
                    })
                );

                btn.classList.toggle('is-unavailable', !hasAvailable);
            });
        });
    }

    updateTotal() {
        if (!this.totalElement) return;

        let total = 0;
        this.selectedProducts.forEach(product => {
            total += product.price;
        });

        this.totalElement.textContent = this.formatMoney(total);
    }

    formatMoney(cents) {
        const amount = cents / 100;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(amount);
    }

    _readCurrentProductPersonalization() {
        const persBlock = document.querySelector('personalizacao-produto-block');
        if (!persBlock) return { filled: false, properties: {} };

        const inputs = persBlock.querySelectorAll('input[name], textarea[name]');
        const properties = {};
        let hasRequiredEmpty = false;

        inputs.forEach(input => {
            const el = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (input);
            if (el.required && !el.value.trim()) {
                hasRequiredEmpty = true;
            }
            const match = el.name.match(/^properties\[(.+)\]$/);
            const propName = match ? match[1] : el.name;
            if (el.value.trim()) {
                properties[propName] = el.value.trim();
            }
        });

        return { filled: !hasRequiredEmpty, properties };
    }

    _getPersonalizacaoQueue() {
        const queue = [];
        const preFilledProperties = new Map();
        const cards = this.querySelectorAll('.comprados-juntos-card');

        cards.forEach(card => {
            const variantId = card.dataset.variantId;
            const hasPersonalization = card.dataset.hasPersonalization === 'true';
            const productUrl = card.dataset.productUrl;
            const productTitle = card.dataset.productTitle;
            const isCurrent = card.classList.contains('comprados-juntos-card--current');

            if (!hasPersonalization) return;
            if (!this.selectedProducts.has(variantId)) return;

            if (isCurrent) {
                const { filled, properties } = this._readCurrentProductPersonalization();
                if (filled) {
                    if (Object.keys(properties).length > 0) {
                        preFilledProperties.set(variantId, { properties, productTitle });
                    }
                } else {
                    queue.push({ variantId, productTitle, productUrl });
                }
                return;
            }

            queue.push({ variantId, productTitle, productUrl });
        });

        return { queue, preFilledProperties };
    }

    setupAddButton() {
        if (!this.addButton) return;

        this.addButton.addEventListener('click', async (event) => {
            event.preventDefault();

            const items = Array.from(this.selectedProducts.values()).map(product => ({
                id: parseInt(product.id, 10),
                quantity: 1
            }));

            if (items.length === 0) return;

            const { queue: personalizacaoQueue, preFilledProperties } = this._getPersonalizacaoQueue();
            let lineItemProperties = new Map(preFilledProperties);

            if (personalizacaoQueue.length > 0) {
                try {
                    const modalProperties = await this.personalizacaoModal.open(personalizacaoQueue);
                    modalProperties.forEach((value, key) => lineItemProperties.set(key, value));
                } catch (err) {
                    return;
                }
            }

            this.addButton.disabled = true;
            this.addButton.textContent = 'Adicionando...';

            const itemsWithProperties = items.map(item => {
                const variantId = String(item.id);
                const persData = lineItemProperties.get(variantId);
                if (persData && Object.keys(persData.properties).length > 0) {
                    return { ...item, properties: persData.properties };
                }
                return item;
            });

            try {
                const response = await fetch(Theme.routes.cart_add_url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ items: itemsWithProperties })
                });

                const data = await response.json();

                if (data.status) {
                    console.error('Erro ao adicionar:', data.message);
                    this.addButton.textContent = data.message || 'Erro ao adicionar';
                    setTimeout(() => {
                        this.addButton.textContent = this.originalButtonText;
                        this.addButton.disabled = false;
                    }, 3000);
                    return;
                }

                this.dispatchCartUpdateEvent(items.length);

                this.addButton.textContent = 'Adicionado!';
                this.addButton.classList.add('atc-added');

                setTimeout(() => {
                    this.addButton.textContent = this.originalButtonText;
                    this.addButton.disabled = false;
                    this.addButton.classList.remove('atc-added');
                }, 2000);

            } catch (error) {
                console.error('Erro ao adicionar produtos:', error);
                this.addButton.textContent = 'Erro ao adicionar';
                setTimeout(() => {
                    this.addButton.textContent = this.originalButtonText;
                    this.addButton.disabled = false;
                }, 2000);
            }
        });
    }

    dispatchCartUpdateEvent(itemCount) {
        const cartUpdateEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
                source: 'comprados-juntos',
                sourceId: this.id || 'comprados-juntos',
                data: {
                    source: 'comprados-juntos',
                    itemCount: itemCount
                }
            }
        });

        document.dispatchEvent(cartUpdateEvent);

        const cartDrawer = document.querySelector('cart-drawer[auto-open]');
        if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
            cartDrawer.showDialog();
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
        }
    }
}

if (!customElements.get('comprados-juntos-component')) {
    customElements.define('comprados-juntos-component', CompradosJuntosComponent);
}
